import { Exact, ListPaymentSourceQuery, PaymentSource, useListPaymentSourceQuery } from '@/types/generated/graphql';
import { QueryResult } from '@apollo/client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export type UsePaymentMethodsListReturn = {
  listSubscriptionResult: QueryResult<
    ListPaymentSourceQuery,
    Exact<{
      [key: string]: never;
    }>
  >;
  paymentMethods: (PaymentSource | null)[];
  paymentCards: (PaymentSource | null)[];
  paymentPaypal: (PaymentSource | null)[];
  paymentMethod: PaymentSource | null;
  defaultPaymentMethod?: PaymentSource | null;
  setPaymentMethod: Dispatch<SetStateAction<PaymentSource | null>>;
  setDefaultPaymentMethod: VoidFunction;
  processing: boolean;
  setProcessing: Dispatch<SetStateAction<boolean>>;
};

export function usePaymentMethodsList(): UsePaymentMethodsListReturn {
  const [paymentMethod, setPaymentMethod] = useState<PaymentSource | null>(null);
  const [processing, setProcessing] = useState(false);

  const listPaymentSourceResult = useListPaymentSourceQuery();

  const paymentMethods = listPaymentSourceResult?.data?.listPaymentSource?.data ?? [];
  const paymentCards = paymentMethods.filter((method) => method?.paypal?.email === null);
  const paymentPaypal = paymentMethods.filter((method) => method?.paypal?.email !== null);
  const defaultPaymentMethod =
    paymentCards.find((method) => method?.isPrimary) || paymentPaypal.find((method) => method?.isPrimary);

  const setDefaultPaymentMethod = () => {
    const defaultMethod = findDefaultPaymentMethod({ paymentCards, paymentPaypal });
    if (defaultMethod) setPaymentMethod(defaultMethod);
  };

  useEffect(() => {
    if (paymentMethods.length > 0 && !paymentMethod) {
      setDefaultPaymentMethod();
    }
  }, [paymentMethods]);

  return {
    listSubscriptionResult: listPaymentSourceResult,
    paymentMethods,
    paymentCards,
    paymentPaypal,
    paymentMethod,
    defaultPaymentMethod,
    setPaymentMethod,
    setDefaultPaymentMethod,
    processing,
    setProcessing,
  };
}

export const findDefaultPaymentMethod = <T extends PaymentSource>(props: {
  paymentCards: (T | null | undefined)[];
  paymentPaypal: (T | null | undefined)[];
}) => {
  return (
    props.paymentCards?.find((card) => card?.isPrimary) || props.paymentPaypal?.find((paypal) => paypal?.isPrimary)
  );
};
